import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { ehComprado, itemFormSchema, temFicha, type AtualizarItemDto, type CriarItemDto, type ItemFormValores, type ItemResponse } from '@triade/contracts';
import { useAtividadesDaEmpresa } from '@/api/atividades';
import { useCustosAquisicao } from '@/api/custos-aquisicao';
import { useArquivarItem, useAtualizarItem, useCriarItem, useItem, useResumoItem, useVizinhosItem } from '@/api/itens';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { SeloProtheus } from '@/components/integracoes-erp/selo-protheus';
import { CodigosExternos } from '@/components/externos/codigos-externos';
import { UsadaEm } from '@/components/ficha-tecnica/usada-em';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { ApiError } from '@/lib/api';
import { formatarDataHora } from '@/lib/format';
import { usePode } from '@/api/me';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { AquisicaoItem } from './aquisicao-item';
import { DadosItem } from './dados-item';
import { HistoricoCustosItem } from './historico-custos-item';
import { RecuperacaoItem } from './recuperacao-item';
import { CustoAquisicaoVigente, FichaEscolhida, PrecosPraticados } from './precificacao-item';
import { SituacaoBadge } from './situacao-badge';
import { deResposta, FORM_VAZIO, paraFiltro, ROTULO_TIPO, situacaoDe, type BuscaItemDetalhe } from './tipos';
import { TributosItem } from './tributos-item';
import { BotaoRelatorios } from '@/relatorios';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

const ABA = 'h-[38px] border-b-2 border-transparent px-3.5 text-ink-secondary hover:text-ink data-[state=active]:border-primary data-[state=active]:font-semibold data-[state=active]:text-ink';

/** Tela "Cadastro de item" (/cadastros/itens/$id e /cadastros/itens/novo). */
export function ItemPage({ id, busca }: { id?: string; busca: BuscaItemDetalhe }) {
  const { empresaId } = useSessao();
  const item = useItem(id);

  let corpo;
  if (empresaId === null) corpo = <Vazio>Escolha a empresa no menu lateral para operar.</Vazio>;
  else if (id && item.isError) corpo = <ErroFormacao erro={item.error} onTentar={() => void item.refetch()} />;
  else if (id && !item.data) corpo = <Vazio papel="status">Carregando item…</Vazio>;
  else corpo = <Editor key={id ?? 'novo'} item={item.data ?? null} busca={busca} />;

  return <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">{corpo}</div>;
}

function Editor({ item, busca }: { item: ItemResponse | null; busca: BuscaItemDetalhe }) {
  const navigate = useNavigate();
  const filtro = useMemo(() => paraFiltro(busca), [busca]);
  const base = useMemo<ItemFormValores>(() => (item ? deResposta(item) : FORM_VAZIO), [item]);
  const form = useForm<ItemFormValores, unknown, ItemFormValores>({ resolver: zodResolver(itemFormSchema), defaultValues: base, mode: 'onTouched' });
  const { isDirty, dirtyFields } = form.formState;

  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [criadoId, setCriadoId] = useState<string | null>(null);
  const [vigenciaSuja, setVigenciaSuja] = useState(false);
  const [aquisicaoSuja, setAquisicaoSuja] = useState(false);
  const [aba, setAba] = useState<string>(busca.aba ?? 'tributacao');

  // Servidor mudou (salvou, arquivou, reativou): o formulario volta a refletir o gravado, se nao houver edicao em curso.
  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const criar = useCriarItem();
  const atualizar = useAtualizarItem();
  const arquivar = useArquivarItem();
  const salvando = criar.isPending || atualizar.isPending;
  const vizinhos = useVizinhosItem(item?.id, filtro);
  const resumo = useResumoItem(item?.id);
  // Permissoes efetivas (GET /me): a API e quem nega; aqui so se esconde o que o usuario nao pode ver ou fazer.
  const { pode } = usePode();
  const atividades = useAtividadesDaEmpresa();
  const comCusto = pode('custo.ver');
  const podeEditar = pode('item.editar');
  const tipo = item?.tipo ?? form.watch('tipo');
  const comprado = ehComprado(tipo);
  // Item vindo do Protheus: descricao, unidade e ativo sao travados (e nao vao no PATCH); arquivar so la.
  const doProtheus = item?.doProtheus === true;
  // Valor de recuperacao (subproduto): so com custo.ver e item.ver (mesma permissao de GET /valores-recuperacao).
  const podeVerRecuperacao = comCusto && pode('item.ver');
  const abaAtiva = aba === 'recuperacao' && !podeVerRecuperacao ? 'tributacao' : aba;
  const custos = useCustosAquisicao({ itemId: item?.id, limit: 50 }, { ativo: comCusto && item !== null && comprado });
  const bloqueio = useBlockerDirty((isDirty || vigenciaSuja || aquisicaoSuja) && criadoId === null);

  useEffect(() => {
    if (criadoId) void navigate({ to: '/cadastros/itens/$id', params: { id: criadoId }, search: filtro, replace: true });
  }, [criadoId, navigate, filtro]);

  const alterados = Object.keys(dirtyFields).length;
  const setarErroDeCampo = useCallback(
    (e: unknown) => {
      if (!(e instanceof ApiError)) return;
      if (e.code === 'CODIGO_DUPLICADO') form.setError('codigo', { message: e.message });
      for (const i of e.body?.issues ?? []) {
        const campo = i.path.split('.')[0] as keyof ItemFormValores;
        if (campo in FORM_VAZIO) form.setError(campo, { message: i.message });
      }
    },
    [form],
  );

  async function gravar(valores: ItemFormValores) {
    setErroSalvar(null);
    try {
      if (item) {
        const corpo: AtualizarItemDto = {
          // Campos do Protheus (descricao, unidade, ativo) nao vao no PATCH: a API recusa a mudanca (409 CAMPO_DO_PROTHEUS).
          ...(doProtheus ? {} : { descricao: valores.descricao, unidade: valores.unidade, ativo: valores.ativo }),
          zfm: valores.zfm,
          compoeFichaTecnica: valores.compoeFichaTecnica ?? false,
          comerciavel: valores.comerciavel ?? false,
          // So PRODUTO tem classificacao; null limpa a que estava gravada.
          ...(item.tipo === 'PRODUTO' ? { classificacao: valores.classificacao ?? null } : {}),
          // Unidade de custo vazia = sem unidade (o fator volta a 1, o padrao da API). Sem unidade nos dois lados, nada e enviado.
          ...(valores.unidadeCusto || item.unidadeCusto ? { unidadeCusto: valores.unidadeCusto ?? null, fatorCustoVenda: valores.fatorCustoVenda ?? '1' } : {}),
          // Categoria so vai se mudou (sem categoria = null). Quem nao mudou nao mexe no vinculo.
          ...((item.categoriaId ?? undefined) !== valores.categoriaId ? { categoriaId: valores.categoriaId ?? null } : {}),
        };
        const gravado = await atualizar.mutateAsync({ id: item.id, ...corpo });
        form.reset(deResposta(gravado));
      } else {
        const corpo: CriarItemDto = {
          tipo: valores.tipo,
          codigo: valores.codigo,
          descricao: valores.descricao,
          unidade: valores.unidade,
          zfm: valores.zfm,
          compoeFichaTecnica: valores.compoeFichaTecnica,
          comerciavel: valores.comerciavel,
          ...(valores.tipo === 'PRODUTO' && valores.classificacao ? { classificacao: valores.classificacao } : {}),
          ...(valores.unidadeCusto ? { unidadeCusto: valores.unidadeCusto } : {}),
          ...(valores.unidadeCusto && valores.fatorCustoVenda ? { fatorCustoVenda: valores.fatorCustoVenda } : {}),
          // Sem categoria = omitido (o POST nao leva null).
          ...(valores.categoriaId ? { categoriaId: valores.categoriaId } : {}),
        };
        const criado = await criar.mutateAsync(corpo);
        form.reset(deResposta(criado));
        setSalvoEm(new Date());
        setCriadoId(criado.id);
        return;
      }
      setSalvoEm(new Date());
    } catch (e) {
      setErroSalvar(mensagemErro(e));
      setarErroDeCampo(e);
    }
  }

  const salvar = () => void form.handleSubmit(gravar, () => setErroSalvar('Corrija os campos destacados'))();

  async function excluir() {
    setErroAcao(null);
    if (!item) {
      void navigate({ to: '/cadastros/itens', search: filtro });
      return;
    }
    try {
      await arquivar.mutateAsync({ id: item.id });
    } catch (e) {
      setErroAcao(mensagemErro(e));
    }
  }

  const irPara = (alvo: string | null | undefined) => {
    if (alvo) void navigate({ to: '/cadastros/itens/$id', params: { id: alvo }, search: filtro });
  };

  const situacao = item ? situacaoDe(item) : null;
  const descricao = form.watch('descricao');
  const codigo = form.watch('codigo');
  const viz = vizinhos.data;

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Cadastros <span aria-hidden="true">/</span>{' '}
            <Link to="/cadastros/itens" search={filtro} className="text-ink-muted hover:underline">
              Produtos e serviços
            </Link>{' '}
            <span aria-hidden="true">/</span> {item ? item.codigo : 'Novo'}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2.5">
            <h1 className="text-display font-semibold tracking-[-0.01em]">{item ? item.descricao : descricao.trim() || 'Novo item'}</h1>
            <span className="rounded-full bg-line-soft px-2.5 py-[3px] text-caption font-semibold text-ink-body">{ROTULO_TIPO[tipo]}</span>
            {situacao && <SituacaoBadge situacao={situacao} />}
            {doProtheus && <SeloProtheus />}
          </div>
        </div>
        {item && <BotaoRelatorios tela="item" params={{ itemId: item.id }} />}
      </div>

      {item?.arquivadoEm && (
        <div role="status" data-testid="aviso-arquivado" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          <b>Item arquivado em {formatarDataHora(item.arquivadoEm)}.</b> Ele some das buscas de itens ativos, mas o histórico (fichas, custos, formações e orçamentos)
          continua intacto. Para reativar, marque <i>Ativo</i> e salve.
        </div>
      )}
      {erroAcao && (
        <div role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          {erroAcao}
        </div>
      )}

      {!podeEditar && (
        <div role="status" data-testid="aviso-somente-leitura" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          <b>Somente leitura.</b> Seu acesso permite consultar o item, não alterá-lo.
        </div>
      )}
      <fieldset disabled={!podeEditar} className="contents">
        <DadosItem form={form} novo={item === null} atividades={atividades} doProtheus={doProtheus} />
      </fieldset>

      <section aria-label="Detalhes" className="rounded-2xl border border-border bg-card">
        {item === null ? (
          <p role="status" className="px-5 py-8 text-center text-small text-ink-muted">
            Salve o item para cadastrar tributos com vigência e ver custos, ficha técnica e preços praticados.
          </p>
        ) : (
          <TabsPrimitive.Root value={abaAtiva} onValueChange={setAba}>
            <TabsPrimitive.List aria-label="Detalhes do item" className="flex gap-1 border-b border-border px-3 pt-2.5">
              <TabsPrimitive.Trigger value="tributacao" className={cn(ABA)}>
                Tributação
              </TabsPrimitive.Trigger>
              {comCusto && comprado && (
                <TabsPrimitive.Trigger value="aquisicao" className={cn(ABA)}>
                  Aquisição
                </TabsPrimitive.Trigger>
              )}
              <TabsPrimitive.Trigger value="precificacao" className={cn(ABA)}>
                Precificação
              </TabsPrimitive.Trigger>
              {comCusto && comprado && (
                <TabsPrimitive.Trigger value="custos" className={cn(ABA)}>
                  Histórico de custos
                </TabsPrimitive.Trigger>
              )}
              {podeVerRecuperacao && (
                <TabsPrimitive.Trigger value="recuperacao" className={cn(ABA)}>
                  Valor de recuperação
                </TabsPrimitive.Trigger>
              )}
            </TabsPrimitive.List>
            <TabsPrimitive.Content value="tributacao" className="outline-none">
              <TributosItem itemId={item.id} form={form} vigente={resumo.data ? resumo.data.tributoVigente : undefined} onDirtyChange={setVigenciaSuja} podeRegistrar={pode('tributo.editar')} />
            </TabsPrimitive.Content>
            {comCusto && comprado && (
              <TabsPrimitive.Content value="aquisicao" className="outline-none">
                <AquisicaoItem
                  itemId={item.id}
                  vigente={resumo.data ? resumo.data.custoAquisicaoVigente : undefined}
                  custos={custos.data}
                  onDirtyChange={setAquisicaoSuja}
                  podeRegistrar={pode('custoAquisicao.editar')}
                  comCusto={comCusto}
                />
              </TabsPrimitive.Content>
            )}
            <TabsPrimitive.Content value="precificacao" className="outline-none">
              {resumo.isError ? (
                <div className="p-5">
                  <ErroFormacao erro={resumo.error} onTentar={() => void resumo.refetch()} />
                </div>
              ) : !resumo.data ? (
                <p role="status" className="p-5 text-small text-ink-muted">
                  Carregando custos e preços…
                </p>
              ) : (
                <div className="flex flex-col gap-4 p-5">
                  {/* A API omite o bloco que o usuario nao pode ver (ficha: ficha.ver; custo de aquisicao: custo.ver; praticados: preco.ver). */}
                  {temFicha(item.tipo) && resumo.data.fichaEscolhida !== undefined && <FichaEscolhida ficha={resumo.data.fichaEscolhida} itemId={item.id} tipo={item.tipo} />}
                  {comprado && resumo.data.custoAquisicaoVigente !== undefined && <CustoAquisicaoVigente tipo={item.tipo} custo={resumo.data.custoAquisicaoVigente} />}
                  {resumo.data.precosPraticados !== undefined && <PrecosPraticados precos={resumo.data.precosPraticados} />}
                  {resumo.data.fichaEscolhida === undefined && resumo.data.custoAquisicaoVigente === undefined && resumo.data.precosPraticados === undefined && (
                    <p className="text-small text-ink-muted">Seu acesso não inclui custos, ficha técnica nem preços praticados deste item.</p>
                  )}
                </div>
              )}
            </TabsPrimitive.Content>
            <TabsPrimitive.Content value="custos" className="outline-none">
              {comCusto && comprado && <HistoricoCustosItem tipo={item.tipo} custos={custos.data} erro={custos.error} onTentar={() => void custos.refetch()} />}
            </TabsPrimitive.Content>
            {podeVerRecuperacao && (
              <TabsPrimitive.Content value="recuperacao" className="outline-none">
                <RecuperacaoItem itemId={item.id} podeRegistrar={pode('custoAquisicao.editar') && comCusto} />
              </TabsPrimitive.Content>
            )}
          </TabsPrimitive.Root>
        )}
      </section>

      {/* Sessao 49 (E): onde o item e usado nas fichas (cascata). So com ficha.ver; a API recusa o resto. */}
      {item && pode('ficha.ver') && (
        <section aria-label="Usado em" className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
          <h2 className="text-title font-semibold">Usado em</h2>
          <UsadaEm itemId={item.id} />
        </section>
      )}

      {item && (
        <section aria-label="Códigos externos" className="rounded-2xl border border-border bg-card p-5">
          <CodigosExternos entidade="itens" id={item.id} editavel={podeEditar} />
        </section>
      )}

      {podeEditar && (
      <RecordToolbar
        dirty={isDirty}
        saving={salvando}
        savedAt={salvoEm}
        erro={erroSalvar}
        novo={item === null}
        index={item && viz ? viz.posicao : undefined}
        total={item && viz ? viz.total : undefined}
        canPrev={Boolean(viz?.anteriorId) && !isDirty}
        canNext={Boolean(viz?.proximoId) && !isDirty}
        alteradosCount={isDirty ? alterados : undefined}
        onList={() => void navigate({ to: '/cadastros/itens', search: filtro })}
        onSave={salvar}
        onNew={() => void navigate({ to: '/cadastros/itens/novo', search: filtro })}
        onUndo={() => {
          form.reset(base);
          setErroSalvar(null);
        }}
        onPrev={() => irPara(viz?.anteriorId)}
        onNext={() => irPara(viz?.proximoId)}
        onDelete={() => void excluir()}
        excluivel={!doProtheus}
        excluirTitulo={item ? `Arquivar o item ${item.codigo}?` : 'Descartar este item?'}
        excluirConfirmar={item ? 'Arquivar' : 'Descartar'}
        excluirDescricao={
          item
            ? 'O item não é apagado: ele pode estar em fichas técnicas, custos, formações de preço e orçamentos, e esse histórico precisa continuar legível. Ele é arquivado (fica inativo e sai das buscas de itens ativos). Para reativar, marque Ativo e salve.'
            : `O item${codigo ? ` ${codigo}` : ''} ainda não foi salvo; sair da tela descarta o que foi digitado.`
        }
      />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}

function Vazio({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
